import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { KnowledgeCategory, KnowledgeArticle, KnowledgeSearchResult } from '@/types/knowledgeBook';
import knowledgeService from '@/services/knowledgeService';

type View =
  | { type: 'categories' }
  | { type: 'articles'; categorySlug: string }
  | { type: 'article'; categorySlug: string; articleSlug: string };

export function useKnowledgeBook() {
  const { i18n } = useTranslation();
  const [view, setView] = useState<View>({ type: 'categories' });
  const [searchQuery, setSearchQuery] = useState('');

  const [categories, setCategories] = useState<KnowledgeCategory[]>([]);
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [currentArticle, setCurrentArticle] = useState<KnowledgeArticle | null>(null);
  const [searchResults, setSearchResults] = useState<KnowledgeSearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch categories on mount and when language changes
  useEffect(() => {
    setView({ type: 'categories' });
    setLoading(true);
    knowledgeService
      .getCategories()
      .then(setCategories)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [i18n.language]);

  // Fetch articles when category view changes
  useEffect(() => {
    if (view.type === 'articles') {
      setLoading(true);
      setError(null);
      knowledgeService
        .getArticles(view.categorySlug)
        .then(setArticles)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [view.type === 'articles' ? view.categorySlug : null]);

  // Fetch single article
  useEffect(() => {
    if (view.type === 'article') {
      setLoading(true);
      setError(null);
      knowledgeService
        .getArticle(view.categorySlug, view.articleSlug)
        .then(setCurrentArticle)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [view.type === 'article' ? `${view.categorySlug}/${view.articleSlug}` : null]);

  // Search with debounce
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(() => {
      knowledgeService
        .search(searchQuery)
        .then(setSearchResults)
        .catch(() => setSearchResults([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const getCategoryBySlug = useCallback(
    (slug: string) => categories.find((c) => c.slug === slug),
    [categories],
  );

  const navigateToCategory = useCallback((slug: string) => {
    setSearchQuery('');
    setView({ type: 'articles', categorySlug: slug });
  }, []);

  const navigateToArticle = useCallback((catSlug: string, artSlug: string) => {
    setSearchQuery('');
    setView({ type: 'article', categorySlug: catSlug, articleSlug: artSlug });
  }, []);

  const navigateToCategories = useCallback(() => {
    setView({ type: 'categories' });
  }, []);

  const navigateToCategoryArticles = useCallback((categorySlug: string) => {
    setView({ type: 'articles', categorySlug });
  }, []);

  return {
    view,
    searchQuery,
    setSearchQuery,
    categories,
    articles,
    currentArticle,
    searchResults,
    loading,
    error,
    getCategoryBySlug,
    navigateToCategory,
    navigateToArticle,
    navigateToCategories,
    navigateToCategoryArticles,
  };
}
